import {
  ComponentPropsWithoutRef,
  forwardRef,
  useState,
  UIEvent,
  useContext,
  useLayoutEffect,
  useRef,
  useCallback,
} from "react";
import ArrowBack from "@servicetitan/hammer-icon/mdi/round/arrow_back.svg";
import ArrowForward from "@servicetitan/hammer-icon/mdi/round/arrow_forward.svg";
import { Icon } from "../Icon";

import cx from "classnames";
import styles from "./Tab.module.scss";
import { TabContext } from "./internal/TabContext";

/**
 * Props for the TabList component
 * @extends ComponentPropsWithoutRef<"div">
 */
export type TabListProps = ComponentPropsWithoutRef<"div">;

/**
 * TabList component for containing and managing tab buttons.
 *
 * Features:
 * - Horizontal scrolling for overflow tabs
 * - Navigation arrows for overflow content
 * - RTL language support
 * - Automatic overflow detection
 * - Proper ARIA roles for accessibility
 * - Smooth scrolling behavior
 * - Visual indicator positioning
 *
 * @example
 * <Tab.List>
 *   <Tab.Button id="tab1" controls="panel1">First Tab</Tab.Button>
 *   <Tab.Button id="tab2" controls="panel2">Second Tab</Tab.Button>
 *   <Tab.Button id="tab3" controls="panel3">Third Tab</Tab.Button>
 * </Tab.List>
 */
export const TabList = forwardRef<HTMLDivElement, TabListProps>(
  (props, ref) => {
    const {
      className,
      children,
      onKeyDown,
      "aria-label": ariaLabel,
      "aria-labelledby": ariaLabelledBy,
      ...rest
    } = props;
    const { goToPrev, gotoNext, dir } = useContext(TabContext);
    const [leftOverflow, setLeftOverflow] = useState(false);
    const [rightOverflow, setRightOverflow] = useState(false);
    const scrollRef = useRef<HTMLDivElement>(null);
    const [indicatorPos, _setIndicatorPos] = useState<{
      left: number;
      width: number;
    }>();

    const calculateOverflow = useCallback(
      (el: HTMLElement) => {
        const leftOverflows =
          dir === "ltr"
            ? el.scrollLeft > 0
            : el.scrollLeft > el.offsetWidth - el.scrollWidth + 1;
        const rightOverflows =
          dir === "ltr"
            ? el.scrollLeft < el.scrollWidth - el.offsetWidth
            : el.scrollLeft < 0;
        setLeftOverflow(leftOverflows);
        setRightOverflow(rightOverflows);
      },
      [dir],
    );

    useLayoutEffect(() => {
      if (!scrollRef.current) return;
      const observer = new ResizeObserver((entries) => {
        for (const entry of entries) {
          calculateOverflow(entry.target as HTMLElement);
        }
      });

      observer.observe(scrollRef.current);

      // Cleanup function
      return () => {
        observer.disconnect();
      };
    }, [calculateOverflow]);

    // update overflow position on scroll
    const overflowPosition = (e: UIEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement;
      calculateOverflow(target);
    };

    const tabListClassNames = cx(styles["tab-list"], className);

    const scrollerClassNames = cx(styles["scroller"], {
      [styles["left"]]: leftOverflow,
      [styles["right"]]: rightOverflow,
    });

    return (
      <div
        {...rest}
        className={tabListClassNames}
        data-anv="tab-list"
        ref={ref}
      >
        {(dir === "ltr" ? leftOverflow : rightOverflow) && (
          <button
            tabIndex={-1}
            onClick={() => goToPrev?.(false)}
            className={styles["overflow-prev"]}
            aria-label="Previous tab"
          >
            <Icon svg={ArrowBack} aria-hidden size="medium" />
          </button>
        )}
        <div
          onScroll={overflowPosition}
          onKeyDown={onKeyDown}
          className={scrollerClassNames}
          role="tablist"
          tabIndex={-1}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledBy}
          ref={scrollRef}
        >
          {children}
          <span
            className={styles["indicator"]}
            style={{
              insetInlineStart: dir === "ltr" ? indicatorPos?.left : undefined,
              insetInlineEnd: dir === "rtl" ? indicatorPos?.left : undefined,
              width: indicatorPos?.width,
            }}
          />
        </div>
        {(dir === "ltr" ? rightOverflow : leftOverflow) && (
          <button
            tabIndex={-1}
            onClick={() => gotoNext?.(false)}
            className={styles["overflow-next"]}
            aria-label="Next tab"
          >
            <Icon svg={ArrowForward} aria-hidden size="medium" />
          </button>
        )}
      </div>
    );
  },
);

TabList.displayName = "TabList";
